import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';

const AuthContext = createContext({});

// Helper functions to persist user info with an expiry timestamp (in milliseconds)
const STORAGE_KEY = 'authUser';
const EXPIRY_DAYS = 3;
const DEV_MODE = process.env.REACT_APP_SILO_DEV_MODE === 'true';

const setPersistedUser = (user) => {
 const expiryTimestamp = new Date().getTime() + EXPIRY_DAYS * 24 * 60 * 60 * 1000;
 const data = { user, expiry: expiryTimestamp };
 localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

const getPersistedUser = () => {
 const stored = localStorage.getItem(STORAGE_KEY);
 if (stored) {
   try {
     const { user, expiry } = JSON.parse(stored);
     // Check if the stored item is still valid
     if (new Date().getTime() < expiry) {
       return user;
     } else {
       // Expired; remove it
       localStorage.removeItem(STORAGE_KEY);
     }
   } catch (error) {
     console.error('Error parsing stored user:', error);
     localStorage.removeItem(STORAGE_KEY);
   }
 }
 return null;
};

export function AuthProvider({ children }) {
 // On initialization, load the persisted user if available
 const [user, setUser] = useState(getPersistedUser());
 const [isLoading, setIsLoading] = useState(false);
 const [error, setError] = useState(null);

 const login = useGoogleLogin({
   onSuccess: async (tokenResponse) => {
     console.log('Google login successful:', tokenResponse);
     try {
       setIsLoading(true);
       setError(null);

       const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
         headers: { 'Authorization': `Bearer ${tokenResponse.access_token}` }
       });
       const userInfo = await userInfoResponse.json();
       console.log('Google user info:', userInfo);

       const requestBody = {
         email: userInfo.email,
         name: userInfo.name,
         picture: userInfo.picture
       };
       console.log('Sending to backend:', requestBody);

       const response = await fetch('/be/api/v1/auth/google', {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify(requestBody),
         credentials: 'include'
       });
       console.log('Backend response status:', response.status);
       
       if (!response.ok) {
         const errorData = await response.json();
         console.log('Backend error:', errorData);
         throw new Error(JSON.stringify(errorData));
       }

       const userData = await response.json();
       console.log('Backend success response:', userData);
       setUser(userData);
       // Persist the successful login data with expiration
       setPersistedUser(userData);
     } catch (err) {
       console.error('Error in auth flow:', err);
       setError(err.message);
     } finally {
       setIsLoading(false);
     }
   },
   onError: (error) => {
     console.error('Google login error:', error);
     setError(error.message || 'Google login failed');
   },
   scope: 'email profile'
 });

 const devLogin = useCallback(async () => {
   try {
     setIsLoading(true);
     setError(null);

     const response = await fetch('/be/api/v1/auth/dev', {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       credentials: 'include'
     });

     if (!response.ok) {
       throw new Error('Dev mode authentication failed');
     }

     const userData = await response.json();
     console.log('Dev mode login successful:', userData);
     setUser(userData);
     setPersistedUser(userData);
   } catch (err) {
     console.error('Error in dev auth flow:', err);
     setError(err.message);
   } finally {
     setIsLoading(false);
   }
 }, []);

 const logout = useCallback(() => {
   console.log('Logging out');
   setUser(null);
   localStorage.removeItem(STORAGE_KEY);

   // In dev mode, automatically log back in
   if (DEV_MODE) {
     console.log('Dev mode - auto-logging back in...');
     setTimeout(() => devLogin(), 100);
   }
 }, [devLogin]);

 // Auto-login in dev mode if not already authenticated
 useEffect(() => {
   if (DEV_MODE && !user && !isLoading) {
     console.log('Dev mode enabled - auto-logging in...');
     devLogin();
   }
 }, []); // Only run on mount

 return (
   <AuthContext.Provider
     value={{
       user,
       login,
       logout,
       isLoading,
       error,
       isAuthenticated: !!user,
       isAdmin: user?.admin,
       devMode: DEV_MODE
     }}
   >
     {children}
   </AuthContext.Provider>
 );
}

export const useAuth = () => useContext(AuthContext);